import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ModalHeader from 'modals/layout/ModalHeader/ModalHeader';
import {
  ModalContent,
  ModalNavigation,
  ModalScrollable,
  ModalWrapper,
  PressStud
} from 'v1/components/shared';

import styles from './MobileCalendarHint.module.scss';

const createMobileHintMediaQuery = () =>
  window.matchMedia('(pointer: coarse), (max-width: 767px)');

const isMobileViewport = () => {
  if (typeof window.matchMedia === 'function') {
    return createMobileHintMediaQuery().matches;
  }
  return window.innerWidth <= 767;
};

const hasDismissedHint = () => {
  try {
    return (
      window.localStorage.getItem(
        'ResourcePortal.Calendar.MobileHintDismissed'
      ) === 'true'
    );
  } catch {
    return false;
  }
};

const markHintDismissed = () => {
  try {
    window.localStorage.setItem(
      'ResourcePortal.Calendar.MobileHintDismissed',
      'true'
    );
  } catch {
    // Ignore private browsing or disabled storage; the current session still dismisses.
  }
};

const MobileCalendarHint = ({ isOpen = false, onClose }) => {
  const { t } = useTranslation('resource-portal');
  const [isAutoVisible, setIsAutoVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateVisibility = () => {
      setIsAutoVisible(isMobileViewport() && !hasDismissedHint());
    };

    updateVisibility();

    if (typeof window.matchMedia !== 'function') {
      window.addEventListener('resize', updateVisibility);
      return () => window.removeEventListener('resize', updateVisibility);
    }

    const mediaQuery = createMobileHintMediaQuery();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateVisibility);
      return () => mediaQuery.removeEventListener('change', updateVisibility);
    }

    mediaQuery.addListener(updateVisibility);
    return () => mediaQuery.removeListener(updateVisibility);
  }, []);

  const dismiss = () => {
    markHintDismissed();
    setIsAutoVisible(false);
    onClose?.();
  };

  if (!isOpen && !isAutoVisible) return null;

  return (
    <div
      aria-label={t('CalendarRoute.mobileHint.title')}
      aria-modal="true"
      className={styles.MobileCalendarHint}
      role="dialog"
    >
      <ModalWrapper
        className={styles.MobileCalendarHint__modal}
        hideClose
        size="XS"
      >
        <ModalHeader title={t('CalendarRoute.mobileHint.title')} />
        <ModalContent>
          <ModalScrollable padding="M">
            <p className={styles.MobileCalendarHint__intro}>
              {t('CalendarRoute.mobileHint.intro')}
            </p>
            <ul className={styles.MobileCalendarHint__items}>
              <li>{t('CalendarRoute.mobileHint.items.selectStatus')}</li>
              <li>{t('CalendarRoute.mobileHint.items.adjustHours')}</li>
              <li>{t('CalendarRoute.mobileHint.items.moveOrResize')}</li>
            </ul>
          </ModalScrollable>
          <ModalNavigation>
            <PressStud
              action={dismiss}
              appearance="primary"
              label={t('CalendarRoute.mobileHint.dismiss')}
            />
          </ModalNavigation>
        </ModalContent>
      </ModalWrapper>
    </div>
  );
};

MobileCalendarHint.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

export default MobileCalendarHint;
